import { partnerType as partnerTypeEnum } from '../../common/partnerType'

// Action Type Constants
export const RESET_FORM_DATA = 'form//RESET_FORM_DATA'
export const SAVE_FORM_DATA = 'form//SAVE_FORM_DATA'
export const SAVE_CUSTOMER_FORM_DATA = 'form//SAVE_CUSTOMER_FORM_DATA'
export const SAVE_CUSTOMER_ADDRESS_FORM_DATA =
  'form//SAVE_CUSTOMER_ADDRESS_FORM_DATA'
export const SAVE_CUSTOMER_CONTACT_FORM_DATA =
  'form//SAVE_CUSTOMER_CONTACT_FORM_DATA'
export const SAVE_CUSTOMER_PAYMENT_FORM_DATA =
  'form//SAVE_CUSTOMER_PAYMENT_FORM_DATA'
export const SAVE_OCCUPATION_FORM_DATA = 'form//SAVE_OCCUPATION_FORM_DATA'
export const SAVE_CUSTOMER_DOCUMENTS_FORM_DATA =
  'form//SAVE_CUSTOMER_DOCUMENTS_FORM_DATA'
export const SAVE_CUSTOMER_SALARY_SLIPS_DATA =
  'form//SAVE_CUSTOMER_SALARY_SLIPS_DATA'

// Interfaces for Customer Data
interface PersonalId {
  typeOfDocument?: string
  id?: string
  validUntil?: string
  issuedBy?: string
  issuingDate?: string
  issuingProvince?: string
  issuingPlace?: string
}

interface LocalAddresses {
  streetAndHouseNumber?: string
  province?: string
  zipCode?: string
  place?: string
}

interface ContactData {
  email?: string
  phoneNumber?: string
  mobilePhoneCountryCode?: string
  mobilePhoneNumber?: string
}

interface PaymentData {
  [key: string]: any
}

interface EmploymentData {
  [key: string]: any
}

interface CustomerDocuments {
  salarySlips?: string[]
  [key: string]: any
}

interface CompanyData {
  [key: string]: any
}

export interface CustomerData {
  personalId?: PersonalId
  localAddresses?: LocalAddresses
  contactData?: ContactData
  paymentData?: PaymentData
  partnerType?: string
  employment?: EmploymentData
  salutation?: string
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  provinceOfBirth?: string
  placeOfBirth?: string
  taxId?: string
  maritalStatus?: string
  companyData?: CompanyData
}

interface FormState {
  data: {
    customerData: CustomerData
    customerDocuments: CustomerDocuments
    [key: string]: any // Extendable data object
  }
}

interface Action<T = any> {
  type: string
  payload?: T
}

// Action Creators
export function saveFormData(data: any): Action<FormState['data']> {
  return {
    type: SAVE_FORM_DATA,
    payload: data,
  }
}

export function saveCustomerFormData(data: CustomerData): Action<CustomerData> {
  return {
    type: SAVE_CUSTOMER_FORM_DATA,
    payload: data,
  }
}

export function saveCustomerAddressFormData(
  data: LocalAddresses
): Action<LocalAddresses> {
  return {
    type: SAVE_CUSTOMER_ADDRESS_FORM_DATA,
    payload: data,
  }
}

export function saveCustomerContactFormData(
  data: ContactData
): Action<ContactData> {
  return {
    type: SAVE_CUSTOMER_CONTACT_FORM_DATA,
    payload: data,
  }
}

export function saveCustomerPaymentFormData(
  data: PaymentData
): Action<PaymentData> {
  return {
    type: SAVE_CUSTOMER_PAYMENT_FORM_DATA,
    payload: data,
  }
}

export function saveCustomerEmploymentFormData(
  data: EmploymentData
): Action<EmploymentData> {
  return {
    type: SAVE_OCCUPATION_FORM_DATA,
    payload: data,
  }
}

export function saveCustomerSalarySlipsData(data: string[]): Action<string[]> {
  return {
    type: SAVE_CUSTOMER_SALARY_SLIPS_DATA,
    payload: data,
  }
}

export function saveCustomerDocumentsFormData(
  data: CustomerDocuments
): Action<CustomerDocuments> {
  return {
    type: SAVE_CUSTOMER_DOCUMENTS_FORM_DATA,
    payload: data,
  }
}

export function resetFormData(): Action {
  return {
    type: RESET_FORM_DATA,
  }
}

export function getFormData(store: { form: FormState }): FormState['data'] {
  return store.form.data
}

export function getFormCustomer(store: {
  form: FormState
}): Omit<FormState['data'], 'data'> {
  const { form } = store
  const { data, ...rest } = form
  return rest
}

// Initial State
const initialState: FormState = {
  data: {
    customerData: {
      personalId: {},
      localAddresses: {},
      contactData: {},
      paymentData: {},
      partnerType: undefined,
      employment: {},
      salutation: undefined,
      firstName: undefined,
      lastName: undefined,
      dateOfBirth: undefined,
      provinceOfBirth: undefined,
      placeOfBirth: undefined,
      taxId: undefined,
      maritalStatus: undefined,
    },
    customerDocuments: {},
  },
}

// Reducer
export default function(state = initialState, action: Action): FormState {
  switch (action.type) {
    case RESET_FORM_DATA:
      return {
        ...state,
        //@ts-ignore
        data: {},
      }

    case SAVE_FORM_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          ...action.payload,
        },
      }

    case SAVE_CUSTOMER_DOCUMENTS_FORM_DATA:
      const { storefrontId, ...documents } = action.payload as CustomerDocuments
      return {
        ...state,
        data: {
          ...state.data,
          storefrontId,
          customerDocuments: {
            ...state.data.customerDocuments,
            ...documents,
          },
        },
      }

    case SAVE_CUSTOMER_FORM_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          customerData: {
            ...state.data.customerData,
            ...action.payload,
          },
        },
      }

    case SAVE_CUSTOMER_ADDRESS_FORM_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          customerData: {
            ...state.data.customerData,
            localAddresses: {
              ...state.data.customerData.localAddresses,
              ...action.payload,
            },
          },
        },
      }

    case SAVE_CUSTOMER_CONTACT_FORM_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          customerData: {
            ...state.data.customerData,
            contactData: {
              ...state.data.customerData.contactData,
              ...action.payload,
            },
          },
        },
      }

    case SAVE_CUSTOMER_PAYMENT_FORM_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          customerData: {
            ...state.data.customerData,
            paymentData: {
              ...state.data.customerData.paymentData,
              ...action.payload,
            },
          },
        },
      }

    case SAVE_CUSTOMER_SALARY_SLIPS_DATA:
      return {
        ...state,
        data: {
          ...state.data,
          customerDocuments: {
            ...state.data.customerDocuments,
            salarySlips: action.payload as string[],
          },
        },
      }

    case SAVE_OCCUPATION_FORM_DATA:
      const {
        partnerType,
        ...incomingData
      } = action.payload as EmploymentData & { partnerType: string }
      if (partnerType === partnerTypeEnum.BUSINESS) {
        return {
          ...state,
          data: {
            ...state.data,
            customerData: {
              ...state.data.customerData,
              partnerType,
              companyData: {
                ...(state.data.customerData as any).companyData,
                ...incomingData,
              },
            },
          },
        }
      }

      return {
        ...state,
        data: {
          ...state.data,
          customerData: {
            ...state.data.customerData,
            partnerType,
            employment: {
              ...state.data.customerData.employment,
              ...incomingData,
            },
          },
        },
      }

    default:
      return state
  }
}
